import _ from 'underscore';
import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ConfirmDialog } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

const DeleteConfirmDialog = ({
  title,
  showConfirmDialog,
  setShowConfirmDialog,
  deleteApiAsync,
  i18n,
  warningMsg,
}) => {
  const [showUIBlocker, setShowUIBlocker] = useState(false);

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setShowUIBlocker(false);
  };

  const onDelete = () => {
    setShowUIBlocker(true);
    deleteApiAsync().finally(() => {
      closeConfirmDialog();
    });
  };

  const onCancel = () => {
    closeConfirmDialog();
  };

  return showConfirmDialog ? (
    <ConfirmDialog
      isDisplayed={showConfirmDialog}
      title={title}
      warningMsg={warningMsg}
      confirmString={i18n.getString(_TL_('Delete'))}
      cancelString={i18n.getString(_TL_('Cancel'))}
      onClickConfirm={onDelete}
      onClickCancel={onCancel}
      isUiBlocked={showUIBlocker}
      customStyle={{ zIndex: 1100000, background: 'rgba(255, 255, 255, 0.4)', overflowWrap: 'break-word' }}
    />
  ) : null;
};

DeleteConfirmDialog.propTypes = {
  title: PropTypes.string.isRequired,
  showConfirmDialog: PropTypes.bool.isRequired,
  setShowConfirmDialog: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  deleteApiAsync: PropTypes.func.isRequired,
  warningMsg: PropTypes.string,
};

DeleteConfirmDialog.defaultProps = {
  warningMsg: '',
};

export const useDeleteConfirmDialog = (i18n) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const deletedItems = useRef([]).current;

  const open = () => {
    setShowConfirmDialog(true);
  };

  const render = useCallback(({
    title, deleteApiAsync, onSuccess, onError, warningMsg,
  }) => {
    const onItemDeleted = (item) => {
      deletedItems.push(item);
    };

    const deleteApiAsyncWrapper = () => {
      deletedItems.length = 0;
      return deleteApiAsync({ onItemDeleted })
        .then(() => onSuccess({ deletedItems }))
        .catch(({ uxMessage }) => onError(i18n.getString(uxMessage)));
    };

    return (<DeleteConfirmDialog
      title={title}
      showConfirmDialog={showConfirmDialog}
      setShowConfirmDialog={setShowConfirmDialog}
      deleteApiAsync={deleteApiAsyncWrapper}
      warningMsg={warningMsg}
      i18n={i18n}
    />);
  }, [deletedItems, i18n, showConfirmDialog]);

  return {
    open,
    render,
  };
};

export const useDeleteFolderConfirmDialog = (i18n, logger) => {
  const folderItemRef = useRef();

  const { open, render } = useDeleteConfirmDialog(i18n);

  const openConfirmDialog = (item) => {
    folderItemRef.current = item;
    open();
  };

  const renderConfirmDialog = useCallback((deleteFolderAsync, onSuccess, onError) => {
    const { folderId, folderName } = folderItemRef.current || {};

    const title = i18n.getString(_TL_('Are you sure you want to delete the "{{folderName}}" folder?'), { folderName });

    const deleteApiAsync = ({ onItemDeleted }) => {
      logger.logTrace('Confirm to delete folder', { folderName });
      return deleteFolderAsync({ folderId, folderName, onItemDeleted });
    };

    const onSuccessWrapper = ({ deletedItems: items }) => {
      const deletedItems = _.filter(items, itm => itm.err && !itm.err.ignorable);
      const hasErrors = deletedItems.length > 0;
      if (hasErrors) {
        logger.logTrace('folder is deleted with some errors', { folderName });
      } else {
        logger.logTrace('folder is deleted successfully', { folderName });
      }
      onSuccess({ folderName, folderId, deletedItems });
    };

    return render({
      title, i18n, deleteApiAsync, onSuccess: onSuccessWrapper, onError,
    });
  }, [i18n, render, logger]);

  return {
    open: openConfirmDialog,
    render: renderConfirmDialog,
  };
};

export const useBatchDeleteConfirmDialog = (i18n, logger) => {
  const { open, render } = useDeleteConfirmDialog(i18n);

  const renderConfirmDialog = useCallback((batchDeleteAsync, onSuccess, onError, warningMsg) => {
    const title = i18n.getString(_TL_('Are you sure you want to delete the selected items?'));

    const deleteApiAsync = ({ onItemDeleted }) => {
      logger.logTrace('Confirm to batch delete');
      return batchDeleteAsync({ onItemDeleted });
    };

    const onSuccessWrapper = ({ deletedItems: items }) => {
      const deletedItems = _.filter(items, itm => itm.err && !itm.err.ignorable);
      const hasErrors = deletedItems.length > 0;
      if (hasErrors) {
        logger.logTrace('Batch deleting is done with some errors.');
      } else {
        logger.logTrace('Batch deleting is done successfully.');
      }
      const folderCount = items ? _.filter(items, itm => !!itm.folderId).length : 0;
      const assetCount = items ? _.filter(items, itm => !itm.folderId).length : 0;
      onSuccess({ deletedItems, folderCount, assetCount });
    };

    return render({
      title, i18n, deleteApiAsync, onSuccess: onSuccessWrapper, onError, warningMsg,
    });
  }, [i18n, logger, render]);

  return {
    open,
    render: renderConfirmDialog,
  };
};
